import { Badge, Box, HStack, SimpleGrid, Stack, Text, VStack } from '@chakra-ui/react'
import { useStore } from '@tanstack/react-store'
import { CountryWidget } from 'entities/User/UserCard/blocks/CountryWidget'
import { PrivateBox, getAccess } from 'features/Private/Private'
import isEmpty from 'lodash/isEmpty'
import { useHistory } from 'react-router-dom'
import {
  ROLES,
  TYPE_ENABLED_COLOR,
  TYPE_USER_COUNTRY,
  TYPE_USER_PLATFORM,
  getTypeStatus,
  getTypeStatusTitle,
  typeStatus,
  userIdentification,
  userIdentificationColor,
  userIdentificationLabel,
} from 'shared/constants/user'
import { authStore } from 'shared/store/auth'
import {
  getDateReadFormat,
  getDateTimeReadFormat,
  getDateTimeReadFormatMoscow,
} from 'shared/utils/dateFormatting'
import { Button } from 'uikit'
import {
  ContentContainer,
  ContentContainerColumn,
  GridField,
  GridFieldHeading,
  GridFieldItem,
  RightAside,
} from 'uikit/layout'
import { ActivateUser } from './ActivateUser/ActivateUser'
import { ApproveIdentificationModal } from './ApproveIdentificationModal/ApproveIdentificationModal'
import { ChangeIDXState } from './ChangeIDXState/ChangeIDXState'
import { ChangeUserIdentification } from './ChangeUserIdentification/ChangeUserIdentification'
import { DeclineIdentificationModal } from './DeclineIdentificationModal/DeclineIdentificationModal'
import { DisactivateUser } from './DisactivateUser/DisactivateUser'
import { RemoveUserModal } from './RemoveUserModal/RemoveUserModal'
import { ResetUserIdentification } from './ResetUserIdentification/ResetUserIdentification'
import { TrustUser } from './TrustUser/TrustUser'
import { useFetchAccountsInfo } from './hooks/useFetchAccountsInfo'

export const User = ({ userId, data = {}, isLoading, onUpdateEkeng, ekengIsUpdating }) => {
  const { user } = useStore(authStore)
  const isAdmin = getAccess(user.roles, [ROLES.admin])
  const history = useHistory()
  const status = getTypeStatus(data)
  const isReadyItem = !isEmpty(data)
  const hasEkeng = !!data?.ekengData
  const {
    mutate: fetchAccountsInfo,
    isPending: accountsInfoIsLoading,
    data: accountsInfo,
  } = useFetchAccountsInfo()

  return (
    <ContentContainer>
      <ContentContainerColumn>
        <GridField>
          {hasEkeng && (
            <SimpleGrid minHeight="40px" columns={3}>
              <Box />
              <Box />
              <HStack mb="10px" justify="space-between">
                <Text fontWeight="bold">Ekeng</Text>
                <Button size="sm" onClick={onUpdateEkeng} isLoading={ekengIsUpdating}>
                  Обновить
                </Button>
              </HStack>
            </SimpleGrid>
          )}
          <GridFieldItem
            label="Имя"
            isLoading={isLoading}
            isReady={isReadyItem}
            ekeng={data?.ekengData?.englishFirstName}
            hasEkeng={hasEkeng}
          >
            {data?.firstName}
          </GridFieldItem>
          <GridFieldItem
            label="Фамилия"
            isLoading={isLoading}
            isReady={isReadyItem}
            ekeng={data?.ekengData?.englishLastName}
            hasEkeng={hasEkeng}
          >
            {data?.lastName}
          </GridFieldItem>
          <GridFieldItem
            label="Отчество"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {data?.middleName}
          </GridFieldItem>
          <GridFieldItem
            label="Дата рождения"
            isLoading={isLoading}
            isReady={isReadyItem}
            ekeng={getDateReadFormat(data?.ekengData?.birthDate)}
            hasEkeng={hasEkeng}
          >
            {getDateReadFormat(data?.birthDate)}
          </GridFieldItem>
          <GridFieldItem
            label="ClientID"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {data?.clientId}
          </GridFieldItem>
          {isAdmin && (
            <GridFieldItem
              label="Номер договора"
              isLoading={isLoading}
              isReady={isReadyItem}
              hasEkeng={hasEkeng}
            >
              {data?.contractNumber}
            </GridFieldItem>
          )}
          <GridFieldItem
            label="Номер счета"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {data?.accountNumber}
          </GridFieldItem>
          <GridFieldItem
            label="Дата создания (по Мск)"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {getDateTimeReadFormatMoscow(data?.createDate)}
          </GridFieldItem>
          <GridFieldItem
            label="Часовой пояс"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {!!data?.userTimeZone ? data?.userTimeZone : undefined}
          </GridFieldItem>
        </GridField>
        <GridField>
          <GridFieldHeading>Паспорт</GridFieldHeading>
          <GridFieldItem
            label="Паспорт"
            isLoading={isLoading}
            isReady={isReadyItem}
            ekeng={data?.ekengData?.documentNumber}
            hasEkeng={hasEkeng}
          >
            {data?.passport}
          </GridFieldItem>
          <GridFieldItem
            label="Выдан"
            isLoading={isLoading}
            isReady={isReadyItem}
            ekeng={data?.ekengData?.documentDepartment}
            hasEkeng={hasEkeng}
          >
            {data?.issue}
          </GridFieldItem>
          <GridFieldItem
            label="Дата выдачи"
            isLoading={isLoading}
            isReady={isReadyItem}
            ekeng={getDateReadFormat(data?.ekengData?.issuanceDate)}
            hasEkeng={hasEkeng}
          >
            {getDateReadFormat(data?.dateIssue)}
          </GridFieldItem>
          <GridFieldItem
            label="Дата окончания"
            isLoading={isLoading}
            isReady={isReadyItem}
            ekeng={getDateReadFormat(data?.ekengData?.validityDate)}
            hasEkeng={hasEkeng}
          >
            {null}
          </GridFieldItem>
        </GridField>
        <GridField>
          <GridFieldHeading>Адрес регистрации</GridFieldHeading>
          <GridFieldItem
            label="Страна"
            isLoading={isLoading}
            isReady={isReadyItem}
            ekeng={data?.ekengData?.countryName}
            hasEkeng={hasEkeng}
          >
            {data?.registrationAddress?.addressFields?.country}
          </GridFieldItem>
          <GridFieldItem
            label="Регион"
            isLoading={isLoading}
            isReady={isReadyItem}
            ekeng={data?.ekengData?.region}
            hasEkeng={hasEkeng}
          >
            {data?.registrationAddress?.addressFields?.regionName}
          </GridFieldItem>
          <GridFieldItem
            label="Тип региона"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {data?.registrationAddress?.addressFields?.regionType}
          </GridFieldItem>
          <GridFieldItem
            label="Район"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {data?.registrationAddress?.addressFields?.districtName}
          </GridFieldItem>
          <GridFieldItem
            label="Населенный пункт"
            isLoading={isLoading}
            isReady={isReadyItem}
            ekeng={data?.ekengData?.community ? `Община: ${data?.ekengData?.community}` : undefined}
            hasEkeng={hasEkeng}
          >
            {data?.registrationAddress?.addressFields?.settlementName}
          </GridFieldItem>
          <GridFieldItem
            label="Тип населенного пункта"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {data?.registrationAddress?.addressFields?.settlementType}
          </GridFieldItem>
          <GridFieldItem
            label="Улица"
            isLoading={isLoading}
            isReady={isReadyItem}
            ekeng={data?.ekengData?.street}
            hasEkeng={hasEkeng}
          >
            {data?.registrationAddress?.addressFields?.streetName}
          </GridFieldItem>
          <GridFieldItem
            label="Тип улицы"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {data?.registrationAddress?.addressFields?.streetType}
          </GridFieldItem>
          <GridFieldItem
            label="Дом"
            isLoading={isLoading}
            isReady={isReadyItem}
            ekeng={data?.ekengData?.building}
            hasEkeng={hasEkeng}
          >
            {data?.registrationAddress?.addressFields?.houseNumber}
          </GridFieldItem>
          <GridFieldItem
            label="Номер корпуса"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {data?.registrationAddress?.addressFields?.block}
          </GridFieldItem>
          <GridFieldItem
            label="Номер квартиры"
            isLoading={isLoading}
            isReady={isReadyItem}
            ekeng={data?.ekengData?.apartment}
            hasEkeng={hasEkeng}
          >
            {data?.registrationAddress?.addressFields?.unitNumber}
          </GridFieldItem>
        </GridField>
        {(data?.inn || data?.snils || data?.ssn) && (
          <GridField>
            <GridFieldHeading>Дополнительные документы</GridFieldHeading>
            {data?.inn && (
              <GridFieldItem
                label="ИНН"
                isLoading={isLoading}
                isReady={isReadyItem}
                hasEkeng={hasEkeng}
              >
                {data?.inn}
              </GridFieldItem>
            )}
            {data?.snils && (
              <GridFieldItem
                label="Снилс"
                isLoading={isLoading}
                isReady={isReadyItem}
                hasEkeng={hasEkeng}
              >
                {data?.snils}
              </GridFieldItem>
            )}
            {data?.ssn && (
              <GridFieldItem
                label="TIN"
                isLoading={isLoading}
                isReady={isReadyItem}
                hasEkeng={hasEkeng}
              >
                {data?.ssn}
              </GridFieldItem>
            )}
          </GridField>
        )}
        <GridField>
          <GridFieldHeading>Идентификация</GridFieldHeading>
          <GridFieldItem
            label="Статус"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {userIdentificationLabel[data?.identification]}
          </GridFieldItem>
          <GridFieldItem
            label="Идентификация по IDX"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {data.isIDXApproved === undefined ? undefined : data.isIDXApproved ? 'Да' : 'Нет'}
          </GridFieldItem>
          {data?.identificationError && (
            <GridFieldItem
              label="Код ошибки"
              isLoading={isLoading}
              isReady={isReadyItem}
              hasEkeng={hasEkeng}
            >
              {data?.identificationError}
            </GridFieldItem>
          )}
          <GridFieldItem
            label="Дата идентификации (по Мск)"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {getDateTimeReadFormat(data?.identificationDate)}
          </GridFieldItem>
          <GridFieldItem
            label="Страна"
            isLoading={isLoading}
            isReady={isReadyItem}
            ekeng={data?.ekengData?.countryName}
            hasEkeng={hasEkeng}
          >
            <CountryWidget lang={data?.identityCountry}>
              {TYPE_USER_COUNTRY[data?.identityCountry]}
            </CountryWidget>
          </GridFieldItem>
        </GridField>
        <GridField>
          <GridFieldHeading>Приложение</GridFieldHeading>
          <GridFieldItem
            label="Версия приложения"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {data?.applicationVersion}
          </GridFieldItem>
          <GridFieldItem
            label="Платформа"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {TYPE_USER_PLATFORM[data?.platform]}
          </GridFieldItem>
        </GridField>
        <GridField>
          <GridFieldHeading>Контакты</GridFieldHeading>
          <GridFieldItem
            label="Телефон"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {data?.phone}
          </GridFieldItem>
          <GridFieldItem
            label="E-mail"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {data?.email}
          </GridFieldItem>
        </GridField>
        <GridField>
          <GridFieldHeading>Дополнительная информация</GridFieldHeading>
          <GridFieldItem
            label="ФССП арест"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {data?.fsspArrested === undefined ? undefined : data?.fsspArrested ? 'Да' : 'Нет'}
          </GridFieldItem>
          <GridFieldItem
            label="Добавлен в белый список"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {data?.trusted === undefined ? undefined : data?.trusted ? 'Да' : 'Нет'}
          </GridFieldItem>
          <GridFieldItem
            label="Умер"
            isLoading={isLoading}
            isReady={isReadyItem}
            ekeng={
              data?.ekengData?.isDead === undefined
                ? undefined
                : data?.ekengData?.isDead
                  ? 'Да'
                  : 'Нет'
            }
            hasEkeng={hasEkeng}
          >
            {null}
          </GridFieldItem>
          <GridFieldItem
            alignItems="start"
            label="Комментарии"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {data?.note
              ? String(data?.note)
                  .split('\n')
                  .map((item) => (
                    <Box key={item}>
                      <Text wordBreak="break-word">{item}</Text>
                      <br />
                    </Box>
                  ))
              : undefined}
          </GridFieldItem>
          <GridFieldItem
            label="РКЛ"
            isLoading={isLoading}
            isReady={isReadyItem}
            hasEkeng={hasEkeng}
          >
            {data?.inRkl === undefined ? undefined : data?.inRkl ? 'Да' : 'Нет'}
          </GridFieldItem>
        </GridField>
      </ContentContainerColumn>
      <ContentContainerColumn>
        <Stack gap="24px" position="sticky" top="10px">
          <RightAside>
            <Text fontSize="0.82rem" color="gray.400" mb={2}>
              Идентификация
            </Text>
            <Badge
              minH="50px"
              padding="1px 10px"
              borderRadius={5}
              fontSize="1rem"
              width="100%"
              justifyContent="center"
              pt={3}
              pb={3}
              whiteSpace="none"
              colorPalette={userIdentificationColor[data?.identification] || 'gray'}
            >
              {userIdentificationLabel[data?.identification] || data?.identification}
            </Badge>
            {[userIdentification.SUPPORT_REVIEW, userIdentification.READY].includes(
              data?.identification
            ) && (
              <PrivateBox hasAnyRole={[ROLES.admin, ROLES.user]}>
                <Stack gap={3} pt="20px">
                  <ApproveIdentificationModal userId={userId} />
                  <DeclineIdentificationModal userId={userId} />
                </Stack>
              </PrivateBox>
            )}
            {data?.identification && (
              <PrivateBox hasAnyRole={[ROLES.admin]}>
                <Stack gap={3} pt="20px">
                  {[userIdentification.FULL, userIdentification.FULL_DOSSIER].includes(
                    data?.identification
                  ) && (
                    <ChangeUserIdentification
                      userId={userId}
                      initialValues={data}
                      disabled={isLoading}
                    />
                  )}

                  {data?.identification === userIdentification.UNIDENTIFIED &&
                    data?.identityCountry === 'RUSSIA' && (
                      <ChangeIDXState
                        userId={userId}
                        disabled={isLoading}
                        idxRegistrationStatus={data?.idxRegistrationStatus}
                      />
                    )}

                  <ResetUserIdentification userId={userId} disabled={isLoading} />
                </Stack>
              </PrivateBox>
            )}
          </RightAside>
          <RightAside>
            <Text fontSize="0.82rem" color="gray.400" mb={2}>
              Активность
            </Text>
            <Badge
              minH="40px"
              padding="1px 10px"
              borderRadius={5}
              fontSize="1rem"
              width="100%"
              justifyContent="center"
              pt={3}
              pb={3}
              whiteSpace="none"
              colorPalette={TYPE_ENABLED_COLOR[data?.enabled] || 'gray'}
            >
              {getTypeStatusTitle(data)}
            </Badge>
            <PrivateBox hasAnyRole={[ROLES.admin]}>
              {!isLoading && (
                <Stack gap={3} pt="20px">
                  {status === typeStatus.active && <DisactivateUser userId={userId} />}
                  {status === typeStatus.deactivated && <ActivateUser userId={userId} />}
                  {status !== typeStatus.blocked && <RemoveUserModal userId={userId} />}
                </Stack>
              )}
            </PrivateBox>
          </RightAside>
          <PrivateBox hasAnyRole={[ROLES.admin]}>
            <RightAside>
              <Text fontSize="0.82rem" color="gray.400" mb={2}>
                Лимиты
              </Text>
              <TrustUser userId={userId} trusted={data.trusted} disabled={isLoading} />
            </RightAside>
          </PrivateBox>
          <VStack gap="12px">
            <Button
              width="100%"
              onClick={() => history.push(`/payments/payerPhone=${data?.phone}`)}
              variant="outline"
              disabled={isLoading}
            >
              Отфильтровать в платежах
            </Button>
            <Button
              width="100%"
              onClick={() => fetchAccountsInfo(userId)}
              variant="outline"
              disabled={isLoading}
              loading={accountsInfoIsLoading}
            >
              Показать все счета
            </Button>

            <VStack gap={2} w="full">
              {accountsInfo?.map(({ accountNumber, balanceList }) => {
                const saldo = balanceList.find((balance) => balance.type === 'SALDO')?.value.amount
                const freeNoShadow = balanceList.find(
                  (balance) => balance.type === 'FREE_NO_SHADOW'
                )?.value.amount

                return (
                  <VStack
                    lineHeight="18px"
                    fontSize="14px"
                    key={accountNumber}
                    alignItems="start"
                    gap={0}
                    w="full"
                  >
                    <Text fontWeight="bold">Баланс счета {accountNumber} СКБ:</Text>
                    <Text>Всего средств на счете (Saldo): {saldo}</Text>
                    <Text>Доступные средства (Free no shadow): {freeNoShadow}</Text>
                  </VStack>
                )
              })}
            </VStack>
          </VStack>
        </Stack>
      </ContentContainerColumn>
    </ContentContainer>
  )
}
